import posthog from "posthog-js";
import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";

import { getTutorialSteps } from "@/components/tutorial/tutorialSteps/getTutorialSteps";
import { MAX_NUMBER_OF_CONVERSION_INPUTS } from "@/constants/constants";
import { defaultArtist } from "@/constants/defaultValues";
import {
  conversionNamespace,
  getConversionInputNamespace,
} from "@/constants/namespaces";
import { DEFAULT_TUTORIAL_SAMPLE } from "@/constants/tutorial";
import { hasSeenTutorialTrueAction } from "@/data/auth/actions/hasSeenTutorialTrueAction";
import { useAudioInput } from "@/lib/zustand/useAudioInput";
import { useConversionHyperParameters } from "@/lib/zustand/useConversionHyperParameters";
import { useNamedSelectArtistFilters } from "@/lib/zustand/useSelectArtistFilters";
import { useNamedSelectedArtist } from "@/lib/zustand/useSelectedArtist";
import { findTargetElement } from "@/lib/zustand/useTutorial/findTargetElement";
import { isElementHidden } from "@/lib/zustand/useTutorial/isElementHidden";
import { waitForAnimationsToComplete } from "@/lib/zustand/useTutorial/waitForAnimationsToComplete";
import {
  Artist,
  ConversionConstructed,
  TutorialSample,
  TutorialStep,
  UserNoDates,
} from "@/types/types";

type State = {
  welcomeModalOpen: boolean;
  isActive: boolean;
  currentStepIndex: number;
  tutorialSteps: TutorialStep[];
  // null if between steps (for waiting for animations to complete)
  targetElement: HTMLElement | null;
  // The artist id of the current sample used. This is used to put the artist at the top of the artist list in the select a voice modal
  artistId: Artist["id"];
  // The conversion id of the relevant conversion. Need this to be able to target various elements in the conversion process
  conversionId: ConversionConstructed["id"];
  openWelcomeModal: () => void;
  updateTutorialSteps: (tutorialSample: TutorialSample) => void;
  startTutorial: (simplyUser: boolean) => Promise<void>;
  nextStep: () => Promise<void>;
  endWelcomeModalAndTutorial: (
    user: UserNoDates,
    simplyUser: boolean
  ) => Promise<void>;
};

export const useTutorial = create<State>()((set, get) => {
  // Returns true if the target element is found and visible, false otherwise
  const updateTargetElement = async (targetSelector: string) => {
    const targetElementApp = await findTargetElement(targetSelector);

    if (targetElementApp === null) {
      throw new Error(`Target element not found: ${targetSelector}`);
    }

    await waitForAnimationsToComplete(targetElementApp);

    targetElementApp.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });

    set({ targetElement: targetElementApp });

    // Check wether the target element is visible (for example, the genre filter is not visible on small screens)
    if (isElementHidden(targetElementApp)) {
      return false;
    } else {
      return true;
    }
  };

  const resetRelevantParts = (
    startOrEnd: "start" | "end",
    simplyUser: boolean
  ) => {
    if (startOrEnd === "start") {
      // Reset all conversion input stores
      for (let i = 0; i < MAX_NUMBER_OF_CONVERSION_INPUTS; i++) {
        const clearInput = useAudioInput
          .getState()
          .getNamedStore(getConversionInputNamespace(i))
          .getState().clearInput;

        // Clear any existing input
        clearInput();
      }

      // Reset the conversion hyper parameters
      useConversionHyperParameters.getState().reset(simplyUser);

      // Reset selected artist
      useNamedSelectedArtist
        .getState()
        .getNamedStore(conversionNamespace)
        .getState()
        .setSelectedArtistId(defaultArtist.id);
    }

    // Reset all select voice filters
    useNamedSelectArtistFilters
      .getState()
      .getNamedStore(conversionNamespace)
      .getState()
      .reset();
  };

  const initialConversionId = uuidv4();
  return {
    welcomeModalOpen: false,
    isActive: false,
    currentStepIndex: 0,
    tutorialSteps: getTutorialSteps(
      // Arbitrary sample to start the tutorial
      DEFAULT_TUTORIAL_SAMPLE,
      initialConversionId
    ),
    targetElement: null,
    // Need the artist id to be able to put it at the top of the artist list in the select a voice modal
    artistId: DEFAULT_TUTORIAL_SAMPLE.artist.id,
    // Need the conversion id to be able to watch for different stages of the conversion
    conversionId: initialConversionId,

    openWelcomeModal: () => set({ welcomeModalOpen: true }),
    updateTutorialSteps: (tutorialSample: TutorialSample) => {
      set((state) => ({
        tutorialSteps: getTutorialSteps(tutorialSample, state.conversionId),
        artistId: tutorialSample.artist.id,
      }));
    },

    startTutorial: async (simplyUser: boolean) => {
      // Capture PostHog event for tutorial start
      posthog.capture("startTutorial");

      resetRelevantParts("start", simplyUser);

      // Activate Generate a new conversion id to make sure we're not using the same conversion id as any old tutorial runs
      const conversionId = uuidv4();
      const currentStepIndex = 0;
      const tutorialSteps = getTutorialSteps(
        DEFAULT_TUTORIAL_SAMPLE,
        conversionId
      );
      set({
        welcomeModalOpen: false,
        isActive: true,
        currentStepIndex: currentStepIndex,
        tutorialSteps: tutorialSteps,
        targetElement: null,
        artistId: DEFAULT_TUTORIAL_SAMPLE.artist.id,
        conversionId: conversionId,
      });

      // Update the target element
      const visible = await updateTargetElement(
        tutorialSteps[currentStepIndex].targetSelector
      );

      if (!visible) {
        const state = get();
        state.nextStep();
      }
    },

    nextStep: async () => {
      let state = get();

      // Execute onNextClick callback of the current step if it exists
      const currentStep = state.tutorialSteps[state.currentStepIndex];
      if (currentStep.onNextClick !== null) {
        currentStep.onNextClick();
      }

      const nextStepIndex = state.currentStepIndex + 1;

      // clickTargetOnNext
      if (currentStep.clickTargetOnNext) {
        if (state.targetElement !== null) {
          state.targetElement.click();
        }
      }

      // If we've reached the end of the tutorial, end it
      if (nextStepIndex >= state.tutorialSteps.length) {
        return;
      }

      const nextStepApp = state.tutorialSteps[nextStepIndex];

      // While finding the target element, the target element is null
      set({ targetElement: null });

      // Update the target element
      const visible = await updateTargetElement(nextStepApp.targetSelector);

      set({ currentStepIndex: nextStepIndex });

      // If the target element is not visible, immediately go to the next step
      if (!visible) {
        state = get();
        state.nextStep();
      }
    },

    endWelcomeModalAndTutorial: async (
      user: UserNoDates,
      simplyUser: boolean
    ) => {
      set({ welcomeModalOpen: false, isActive: false });

      resetRelevantParts("end", simplyUser);

      // Update the user's hasSeenTutorial metadata to true
      if (!user.hasSeenTutorial) {
        await hasSeenTutorialTrueAction();
      }
    },
  };
});
